import React, {useRef, useState} from 'react';

interface UseSwipeOptions {
    onSwipeUp?: () => void;
    onSwipeDown?: () => void;
    onSwipeLeft?: () => void;
    onSwipeRight?: () => void;
    threshold?: number;
}

export interface SwipeHandlers {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
    offsetX: number;
    offsetY: number;
    dragging: boolean;
}

/** Lightweight touch-swipe detector - no dependency, single-finger only. */
export function useSwipe(options: UseSwipeOptions): SwipeHandlers {
    const {onSwipeUp, onSwipeDown, onSwipeLeft, onSwipeRight, threshold = 60} = options;
    const start = useRef<{x: number; y: number} | null>(null);
    const [offset, setOffset] = useState({x: 0, y: 0});
    const [dragging, setDragging] = useState(false);

    const onTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        start.current = {x: t.clientX, y: t.clientY};
        setDragging(true);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        if (!start.current) return;
        const t = e.touches[0];
        setOffset({x: t.clientX - start.current.x, y: t.clientY - start.current.y});
    };

    const onTouchEnd = () => {
        if (!start.current) return;
        start.current = null;
        const {x, y} = offset;
        setDragging(false);
        setOffset({x: 0, y: 0});

        const absX = Math.abs(x);
        const absY = Math.abs(y);

        if (absX > absY && absX > threshold) {
            (x > 0 ? onSwipeRight : onSwipeLeft)?.();
            return;
        }
        if (absY > absX && absY > threshold) {
            (y < 0 ? onSwipeUp : onSwipeDown)?.();
        }
    };

    return {onTouchStart, onTouchMove, onTouchEnd, offsetX: offset.x, offsetY: offset.y, dragging};
}
